// @file utility functions related to post drafts
import type { DraftPost, PostAttributes, WishAttachment } from '@@/types'
import { isEqual } from 'es-toolkit'
import { uniqueId } from 'es-toolkit/compat'

export function shouldSyncDraftUpdates({
  newDraft,
  existingDraft,
}: {
  newDraft: DraftPost
  existingDraft?: DraftPost
}): boolean {
  const newAttributes = [
    newDraft.subject,
    newDraft.body?.replace(/&nbsp;/g, ' '),
    newDraft.attachment,
    newDraft.attachment_caption,
    newDraft.wish_content,
    newDraft.color,
    newDraft.wall_section_id,
    newDraft.scheduled_at,
    newDraft.custom_properties,
  ]

  const oldAttributes = [
    existingDraft?.subject,
    existingDraft?.body?.replace(/&nbsp;/g, ' '),
    existingDraft?.attachment,
    existingDraft?.attachment_caption,
    existingDraft?.wish_content,
    existingDraft?.color,
    existingDraft?.wall_section_id,
    existingDraft?.scheduled_at,
    existingDraft?.custom_properties,
  ]

  // convert undefined into null for comparison
  const normalizedNewAttributes = newAttributes.map((v) => (v == null ? null : v))
  const normalizedOldAttributes = oldAttributes.map((v) => (v == null ? null : v))

  return normalizedNewAttributes.some((value, index) => !isEqual(normalizedOldAttributes[index], value))
}

// don't couple cid to c_{id} for drafts. avoid confusion with posts.
export function addDraftCidIfAbsent(postDraft: DraftPost): DraftPost {
  if (postDraft.cid == null) {
    postDraft.cid = uniqueId('c_new')
  }
  return postDraft
}

export const getAttachmentsFromDraft = (draft: PostAttributes): WishAttachment[] => {
  const singleAttachmentUrl = draft.attachment // string | null | undefined
  const hasFirstAttachment = typeof singleAttachmentUrl === 'string' && singleAttachmentUrl.length > 0

  if (draft.attachments != null) {
    const existingAttachments = [...draft.attachments]
    if (hasFirstAttachment) {
      const isSingleAttachmentPresent = existingAttachments.find((att) => att.url === singleAttachmentUrl)
      if (isSingleAttachmentPresent == null) {
        // Prepend single attachment if not present
        return [
          { url: singleAttachmentUrl, alt: draft.wish_content?.attachment_props?.alternative_text },
          ...existingAttachments,
        ]
      }
    }
    return existingAttachments
  } else if (hasFirstAttachment) {
    return [{ url: singleAttachmentUrl, alt: draft.wish_content?.attachment_props?.alternative_text }]
  } else {
    return []
  }
}

export const MAX_PHOTO_ALBUM_ATTACHMENTS = 6
